type RoleInput = string | null | undefined;
type StatusInput = string | null | undefined;

const normalize = (value: string) => value.trim().toLowerCase();

export const isCardiologistRole = (role: RoleInput): boolean =>
  Boolean(role && normalize(role) === 'cardiologist');

export const isDoctorRole = (role: RoleInput): boolean => {
  if (!role) return false;
  const normalizedRole = normalize(role);
  return (
    normalizedRole === 'cardiologist' ||
    normalizedRole === 'radiologist' ||
    normalizedRole === 'clinician'
  );
};

export const isSonographerOrTechnicianRole = (role: RoleInput): boolean => {
  if (!role) {
    return false;
  }
  const normalizedRole = normalize(role);
  return normalizedRole === 'sonographer' || normalizedRole.includes('technician');
};

export const isAdminOrReceptionRole = (role: RoleInput): boolean => {
  if (!role) {
    return false;
  }
  const normalizedRole = normalize(role);
  return (
    normalizedRole === 'admin' ||
    normalizedRole === 'practice manager' ||
    normalizedRole === 'medical receptionist'
  );
};

export const canCreateReport = (role: RoleInput): boolean =>
  isDoctorRole(role) || isSonographerOrTechnicianRole(role);

/**
 * May the report engine write into this report?
 *
 * The chain, the wording rules and the click-to-edit popups all write programmatically,
 * and every one of them used to ask `canEditMedicalData(role, null)` — passing a null
 * status, so the "Finalized reports are locked" branch was never reached. The visual
 * guard on the editor blocks a human's clicks but not a transaction dispatched in code,
 * so opening a Finalized report whose wording disagreed with its measurements could have
 * had a sentence rewritten and autosaved into an already-signed document.
 *
 * The old guards also short-circuited on `isDoctorRole(role) || ...`, which let a doctor
 * through regardless of status. `canEditMedicalData` already grants doctors and
 * sonographers everything they should have, so the role bypass is gone with it.
 */
export const canEngineWriteToReport = (role: RoleInput, status: StatusInput): boolean =>
  canEditMedicalData(role, status);

export const canEditMedicalData = (role: RoleInput, status: StatusInput): boolean => {
  // Finalized reports are locked for everyone — must unfinalize first
  if (status === 'Finalized') return false;
  if (isDoctorRole(role) || isSonographerOrTechnicianRole(role)) {
    return true;
  }
  return false;
};

export const canChangeReportStatus = (role: RoleInput, status?: StatusInput): boolean => {
  if (isDoctorRole(role)) return true;
  // Sonographers can change status only while report is not yet finalized
  if (isSonographerOrTechnicianRole(role)) return status !== 'Finalized';
  return false;
};

/**
 * Checks if the logged-in doctor is assigned to this report.
 * For doctors: they can only edit/finalize/unfinalize reports assigned to them.
 * Non-doctor roles are not affected by this check.
 */
export const isAssignedDoctor = (
  role: RoleInput,
  reportAssignedToId: string | null | undefined,
  loggedInUserId: string | null | undefined,
  reportAssignedToName?: string | null,
  loggedInUserName?: string | null
): boolean => {
  if (!isDoctorRole(role)) return true; // non-doctor roles not restricted by assignment
  // If no assignment info available, allow edit (data not loaded or report unassigned)
  // if (!reportAssignedToId && !reportAssignedToName) return true;
  const idMatch = reportAssignedToId && loggedInUserId && String(reportAssignedToId) === String(loggedInUserId);
  // const nameMatch = reportAssignedToName && loggedInUserName && reportAssignedToName.toLowerCase() === loggedInUserName.toLowerCase();
  return !!(idMatch);
};

/**
 * Checks if a finalized report can be unfinalized by the current user.
 * Only the same doctor who finalized it can unfinalize.
 */
/**
 * Checks if a report can be deleted by the current user.
 * Rules:
 *   1. Only doctor roles can delete
 *   2. Finalized reports cannot be deleted
 *   3. Only the assigned doctor can delete
 */
export const canDeleteReport = (
  role: RoleInput,
  reportStatus: StatusInput,
  reportAssignedToId: string | null | undefined,
  loggedInUserId: string | null | undefined
): boolean => {
  if (!isDoctorRole(role) && !isSonographerOrTechnicianRole(role)) return false;
  if (reportStatus === 'Finalized') return false;
  // Assignment check only applies to doctors — sonographers can delete any non-finalized report
  if (isDoctorRole(role) && reportAssignedToId && loggedInUserId && String(reportAssignedToId) !== String(loggedInUserId)) return false;
  return true;
};

export const canUnfinalizeReport = (
  role: RoleInput,
  reportStatus: StatusInput,
  finalizedById: string | null | undefined,
  loggedInUserId: string | null | undefined
): boolean => {
  if (!isDoctorRole(role)) return false;
  if (reportStatus !== 'Finalized') return false;
  if (!finalizedById || !loggedInUserId) return false;
  return finalizedById === loggedInUserId;
};

/**
 * Regenerate rebuilds the body from the template: doctors and sonographers only, never
 * on a Finalized report, and a doctor only on a report assigned to them. Mirrors the
 * server's /report/regenerate checks, which know no technician role.
 */
export const canRegenerateReport = (
  role: RoleInput,
  reportStatus: StatusInput,
  reportAssignedToId: string | null | undefined,
  loggedInUserId: string | null | undefined
): boolean => {
  if (reportStatus === 'Finalized') return false;
  if (!isDoctorRole(role) && normalize(role || '') !== 'sonographer') return false;
  if (
    isDoctorRole(role) &&
    reportAssignedToId &&
    String(reportAssignedToId) !== String(loggedInUserId)
  ) {
    return false;
  }
  return true;
};
